import React from 'react';

import { useMRFollowupOutcome } from './hooks/useMRFollowupOutcome';
import { useMROutcomeActions } from './hooks/useMROutcomeActions';
import { MRCategoryBadge } from './MRCategoryBadge';
import { MRNextActionPanel } from './MRNextActionPanel';
import { MROutcomeDetails } from './MROutcomeDetails';

interface MRFollowupOutcomeCardProps {
  matterId: string;
  providerId: string;
  requestId: string;
  taskId: string;
}

export const MRFollowupOutcomeCard: React.FC<MRFollowupOutcomeCardProps> = ({
  matterId,
  providerId,
  requestId,
  taskId,
}) => {
  const { outcome, isLoading, hasOutcome, noCallsYet, refetch } =
    useMRFollowupOutcome({
      matterId,
      providerId,
      requestId,
    });

  const actions = useMROutcomeActions({
    matterId,
    providerId,
    requestId,
    taskId,
    onActionComplete: refetch,
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
        <div className="animate-pulse space-y-2">
          <div className="h-4 bg-gray-200 rounded w-32" />
          <div className="h-3 bg-gray-200 rounded w-48" />
        </div>
      </div>
    );
  }

  // No calls yet - show nothing or minimal state
  if (noCallsYet || !hasOutcome || !outcome) {
    return null;
  }

  // Get subcategory description for headline
  const getSubcategoryDescription = () => {
    if (!outcome.subcategory) return null;

    // Map subcategory to human-readable descriptions
    // Only show descriptions for known subcategories - hide raw codes
    const subcategoryDescriptions: Record<string, string> = {
      // Progress made subcategories
      request_confirmed: 'Request confirmed',
      records_ready: 'Records ready for pickup',
      payment_info: 'Payment information received',
      // Action needed subcategories
      wrong_method: 'Wrong submission method',
      payment_required: 'Payment required',
      data_mismatch: 'Patient data mismatch',
      needs_documentation: 'Additional documentation needed',
      agent_performance_issue: 'Call quality issue',
      unknown_issue: 'Issue requires review',
      process_clarification: 'Clarification needed',
      // Auto-retry subcategories
      after_hours: 'Outside business hours',
      voicemail: 'Reached voicemail',
      busy_line: 'Line was busy',
      timing_issue: 'Timing issue',
      no_answer: 'No answer',
      callback_requested: 'Callback requested',
    };

    // Return null for unmapped codes instead of showing raw internal codes
    return subcategoryDescriptions[outcome.subcategory] || null;
  };

  const subcategoryDescription = getSubcategoryDescription();
  // Only show timeline in action panel for progress_made/auto_retry - not for action_needed
  // When action is needed, showing timeline is confusing (suggests waiting vs acting)
  const showTimeline = outcome.category !== 'action_needed';

  const hasVnext = Boolean(outcome.vnext_next_action);
  const topNextAction =
    hasVnext && outcome.vnext_next_action
      ? outcome.vnext_next_action
      : outcome.next_action;
  const topDescription = hasVnext
    ? (outcome.vnext_rationale ?? '')
    : outcome.next_action_description;
  const topShouldTrust = hasVnext ? outcome.vnext_should_trust : null;
  const topIsActionRequired = hasVnext
    ? outcome.vnext_next_actor === 'USER'
    : outcome.category === 'action_needed';

  const topSubcategoryDescription = hasVnext ? null : subcategoryDescription;
  let topTimeline: string | null = null;
  if (!hasVnext && showTimeline) {
    topTimeline = outcome.timeline ?? null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-md p-3 space-y-3">
      {/* Category Badge + Timestamp */}
      <MRCategoryBadge
        category={outcome.category ?? null}
        lastCallAt={outcome.last_call_at ?? null}
      />

      {/* Alternates Panel - show when we have discovered alternates
          Don't show after progress_made (provider confirmed receipt)
          Note: Alternates detail data needs to be fetched separately if needed */}
      {outcome.has_alternates &&
        outcome.alternates_count &&
        outcome.alternates_count > 0 &&
        outcome.category !== 'progress_made' && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-800">
              {outcome.alternates_count} alternate contact
              {outcome.alternates_count > 1 ? 's' : ''} discovered. View request
              details to use alternate contacts.
            </p>
          </div>
        )}

      {/* Next Action Panel */}
      <MRNextActionPanel
        category={outcome.category ?? null}
        nextAction={topNextAction}
        nextActionDescription={topDescription}
        paymentAmount={outcome.payment_amount ?? null}
        subcategoryDescription={topSubcategoryDescription}
        timeline={topTimeline}
        shouldTrust={topShouldTrust}
        isActionRequired={topIsActionRequired}
        onMarkPaid={actions.handleMarkInvoicePaid}
        isMarkPaidLoading={actions.isMarkPaidLoading}
      />

      {/* Expandable Details */}
      <MROutcomeDetails outcome={outcome} />
    </div>
  );
};
